import type { CandidateInterviewAttemptV1 } from '@/shared/api';

type AttemptSnapshot = Pick<
	CandidateInterviewAttemptV1,
	'id' | 'version' | 'serverNow'
>;

export function isOlderAttemptSnapshot(
	previous: AttemptSnapshot | undefined,
	next: AttemptSnapshot,
) {
	return (
		previous?.id.toLowerCase() === next.id.toLowerCase() &&
		(previous.version > next.version ||
			(previous.version === next.version &&
				Date.parse(previous.serverNow) > Date.parse(next.serverNow)))
	);
}

export function remainingSeconds(
	deadline: string | undefined,
	now: number,
	offset: number,
) {
	if (!deadline) return 0;
	return Math.max(0, Math.ceil((Date.parse(deadline) - now - offset) / 1000));
}

export function canWriteCandidate(
	attempt: Pick<
		CandidateInterviewAttemptV1,
		'status' | 'deadlineAt' | 'currentPosition' | 'items'
	>,
	now: number,
	offset: number,
) {
	const item = attempt.items[attempt.currentPosition];
	return (
		attempt.status === 'in_progress' &&
		item?.status === 'active' &&
		remainingSeconds(attempt.deadlineAt, now, offset) > 0 &&
		remainingSeconds(item.deadlineAt, now, offset) > 0
	);
}

export function isSkipCommand(text: string) {
	return ['/skip', '/next'].includes(text.trim().toLowerCase());
}

export function formatRemaining(seconds: number) {
	return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export function autoAdvanceSeconds(
	attempt: Pick<
		CandidateInterviewAttemptV1,
		'status' | 'deadlineAt' | 'currentPosition' | 'items'
	>,
	now: number,
	offset: number,
): number | undefined {
	const item = attempt.items[attempt.currentPosition];
	if (
		attempt.status !== 'in_progress' ||
		!item?.deadlineAt ||
		!['active', 'timed_out'].includes(item.status) ||
		remainingSeconds(attempt.deadlineAt, now, offset) === 0
	)
		return undefined;
	const elapsed = now + offset - Date.parse(item.deadlineAt);
	if (elapsed < 0) return undefined;
	return Math.max(0, Math.ceil((5000 - elapsed) / 1000));
}

export function synchronizeClockOffset(
	previous: number | undefined,
	serverNow: string,
	receivedAt: number,
) {
	const measured = Date.parse(serverNow) - receivedAt;
	// A slower response must not move the estimated server clock backwards.
	return previous === undefined ? measured : Math.max(previous, measured);
}

export function candidateTotalTime(
	attempt: Pick<
		CandidateInterviewAttemptV1,
		'startedAt' | 'deadlineAt' | 'targetDurationSeconds'
	>,
	now: number,
	offset: number,
) {
	if (!attempt.targetDurationSeconds)
		return formatRemaining(remainingSeconds(attempt.deadlineAt, now, offset));
	const elapsedMs = Math.max(0, now + offset - Date.parse(attempt.startedAt));
	const exceeded = elapsedMs > attempt.targetDurationSeconds * 1000;
	return `${formatRemaining(Math.floor(elapsedMs / 1000))} из ${formatRemaining(attempt.targetDurationSeconds)}${exceeded ? ' · лимит превышен' : ''}`;
}
